import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { SystemScanResult } from '../types';
import { 
  Monitor, 
  Code, 
  Bot, 
  Link, 
  ChevronDown, 
  ChevronRight,
  Info,
  ArrowRight
} from 'lucide-react';

interface SystemScanResultsProps {
  scanResult: SystemScanResult;
  onContinue: () => void;
}

export const SystemScanResults: React.FC<SystemScanResultsProps> = ({
  scanResult,
  onContinue,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['integrations']));

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatMemory = (bytes: number) => {
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb.toFixed(1)} GB`;
  };

  const getIntegrationIcon = (type: string) => {
    switch (type) {
      case 'api': return <Link className="w-4 h-4" />;
      case 'file_system': return <Monitor className="w-4 h-4" />;
      case 'database': return <Monitor className="w-4 h-4" />;
      case 'webhook': return <Link className="w-4 h-4" />;
      case 'extension': return <Bot className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'secondary';
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-4">System Scan Complete</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          We've analyzed your Mac and found the following configuration opportunities.
          Review the results and continue to set up ChittyOS.
        </p>
      </div>

      {/* System Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            System Overview
          </CardTitle>
          <CardDescription>Basic system information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">Operating System</p>
              <p className="text-sm text-muted-foreground">
                {scanResult.system_info.os_name} {scanResult.system_info.os_version}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">Architecture</p>
              <p className="text-sm text-muted-foreground">
                {scanResult.system_info.arch}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">Memory</p>
              <p className="text-sm text-muted-foreground">
                {formatMemory(scanResult.system_info.total_memory)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">CPU Cores</p>
              <p className="text-sm text-muted-foreground">
                {scanResult.system_info.cpu_count}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Potential Integrations */}
      <Card>
        <CardHeader 
          className="cursor-pointer"
          onClick={() => toggleSection('integrations')}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link className="w-5 h-5" />
              Potential Integrations
              <Badge variant="secondary">{scanResult.potential_integrations.length}</Badge>
            </div>
            {expandedSections.has('integrations') ? 
              <ChevronDown className="w-5 h-5" /> : 
              <ChevronRight className="w-5 h-5" />
            }
          </CardTitle>
          <CardDescription>
            Services and tools that can be integrated with ChittyOS
          </CardDescription>
        </CardHeader>
        {expandedSections.has('integrations') && (
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {scanResult.potential_integrations.map((integration, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {getIntegrationIcon(integration.integration_type)}
                    <div>
                      <p className="font-medium">{integration.service}</p>
                      <p className="text-sm text-muted-foreground">
                        {integration.description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Detected from: {integration.detected_from.join(', ')}
                      </p>
                    </div>
                  </div>
                  <Badge variant={getConfidenceColor(integration.confidence) as any}>
                    {Math.round(integration.confidence * 100)}%
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Claude Installations */}
      <Card>
        <CardHeader 
          className="cursor-pointer"
          onClick={() => toggleSection('claude')}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5" />
              Claude Installations
              <Badge variant="secondary">{scanResult.claude_installations.length}</Badge>
            </div>
            {expandedSections.has('claude') ? 
              <ChevronDown className="w-5 h-5" /> : 
              <ChevronRight className="w-5 h-5" />
            }
          </CardTitle>
          <CardDescription>
            Found Claude installations and configurations
          </CardDescription>
        </CardHeader>
        {expandedSections.has('claude') && (
          <CardContent>
            <div className="space-y-4">
              {scanResult.claude_installations.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No Claude installations found. ChittyOS will help you set up Claude integration.
                </p>
              ) : (
                scanResult.claude_installations.map((installation, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Bot className="w-5 h-5" />
                      <div>
                        <p className="font-medium">
                          Claude {installation.installation_type}
                          {installation.version && ` v${installation.version}`}
                        </p>
                        <p className="text-sm text-muted-foreground">{installation.path}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {installation.has_config ? (
                        <Badge variant="success">Configured</Badge>
                      ) : (
                        <Badge variant="warning">Needs Setup</Badge>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Development Tools */}
      <Card>
        <CardHeader 
          className="cursor-pointer"
          onClick={() => toggleSection('devtools')}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Code className="w-5 h-5" />
              Development Tools
              <Badge variant="secondary">{scanResult.development_tools.length}</Badge>
            </div>
            {expandedSections.has('devtools') ? 
              <ChevronDown className="w-5 h-5" /> : 
              <ChevronRight className="w-5 h-5" />
            }
          </CardTitle>
          <CardDescription>
            Development tools that can integrate with ChittyOS
          </CardDescription>
        </CardHeader>
        {expandedSections.has('devtools') && (
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {scanResult.development_tools.map((tool, index) => (
                <div key={index} className="p-4 border rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <Code className="w-4 h-4" />
                    <div>
                      <p className="font-medium">{tool.name}</p>
                      {tool.version && (
                        <p className="text-xs text-muted-foreground">v{tool.version}</p>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {tool.tool_type}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Applications */}
      <Card>
        <CardHeader 
          className="cursor-pointer"
          onClick={() => toggleSection('apps')}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Monitor className="w-5 h-5" />
              Applications
              <Badge variant="secondary">{scanResult.applications.length}</Badge>
            </div>
            {expandedSections.has('apps') ? 
              <ChevronDown className="w-5 h-5" /> : 
              <ChevronRight className="w-5 h-5" />
            }
          </CardTitle>
          <CardDescription>
            Installed applications detected on your system
          </CardDescription>
        </CardHeader>
        {expandedSections.has('apps') && (
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
              {scanResult.applications.slice(0, 50).map((app, index) => (
                <div key={index} className="p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Monitor className="w-4 h-4" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{app.name}</p>
                      {app.version && (
                        <p className="text-xs text-muted-foreground">v{app.version}</p>
                      )}
                      {app.category && (
                        <Badge variant="outline" className="text-xs mt-1">
                          {app.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {scanResult.applications.length > 50 && (
                <div className="p-3 border rounded-lg border-dashed">
                  <p className="text-center text-muted-foreground text-sm">
                    +{scanResult.applications.length - 50} more applications
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      <div className="flex justify-center pt-6">
        <Button onClick={onContinue} size="lg" className="gap-2">
          Continue Setup
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};